import axios from "axios";
import { Decrypt, Encrypt } from "./Functions";

export type RelatedProductUpdate = {
  cart_line_id: string;
  store_product_id: number | string;
  unavailable: boolean;
  current: any;
  /** Rewrite this cart row. True when the product is assigned to this bundle. */
  updateCartRow: boolean;
};

export type CartLineChange = {
  cart_line_id: string;
  kind: "product" | "bundle";
  unavailable: boolean;
  changes: string[];
  current: any;
  relatedProductUpdates?: RelatedProductUpdate[];
  /** Built in the cart because a related product changed. Not from the catalog check. */
  synthetic?: boolean;
};

function readCart(): any[] {
  const raw = localStorage?.getItem("cart");
  const decrypted = Decrypt(raw);
  return decrypted ? JSON.parse(decrypted) : [];
}

function writeCart(cart: any[]) {
  localStorage.setItem("cart", Encrypt(JSON.stringify(cart)));
}

function productOptionItemNames(product: any) {
  const names = new Map<string, string>();
  (product?.store_product_options || []).forEach((option: any) => {
    (option?.store_product_option_items || []).forEach((item: any) => {
      if (item?.id != null) names.set(String(item.id), item.option_name || "");
    });
  });
  return names;
}

function snapshotProduct(product: any) {
  const itemNames = productOptionItemNames(product);
  const snapshot: any = {
    id: product?.id,
    product_name: product?.product_name ?? "",
    description: product?.description ?? "",
    style: product?.style ?? "",
    club_price: product?.club_price,
    base_price: product?.base_price,
    minimum_quantity: product?.minimum_quantity,
    maximum_quantity: product?.maximum_quantity,
    is_multiple_quantity: product?.is_multiple_quantity,
    product_shipping_weight: product?.product_shipping_weight ?? "",
    product_shipping_unit: product?.product_shipping_unit ?? "",
    images: (product?.store_product_images || [])
      .filter((image: any) => image && image.deleted_at == null)
      .map((image: any) => ({
        image: image.image || "",
        caption: image.caption || "",
        color_option: image.color_option ?? "",
      })),
    options: (product?.store_options || [])
      .filter((option: any) => option && option.deleted_at == null)
      .map((option: any) => ({
        global_option_id: option.global_option_id,
        display_name: option.display_name || "",
        internal_name: option.internal_name || "",
        option_type: option.option_type,
        input_type: option.input_type,
        selected_item_id: option.selectedData ?? null,
        items: (option.store_option_items || [])
          .filter(
            (item: any) =>
              item && item.deleted_at == null && !item.store_option_item_deleted_at
          )
          .map((item: any) => ({
            id: item.id,
            option_name: itemNames.has(String(item.id))
              ? itemNames.get(String(item.id))
              : item.option_name || "",
          })),
      })),
  };

  // Only send these when the cart actually loaded the association. Bundle
  // products do not, and an empty string would look like a category change.
  if (product?.category) {
    snapshot.category_name = product.category.category_name ?? "";
  }
  if (product?.manufacturer) {
    snapshot.manufacturer_name = product.manufacturer.manufacturer_name ?? "";
  }
  return snapshot;
}

export function buildCartChangeLines(cart: any[]) {
  return (cart || [])
    .map((item) => {
      if (item?.isSingleProduct) {
        return {
          cart_line_id: String(item.cartLineId),
          kind: "product" as const,
          id: Number(item.id),
          snapshot: snapshotProduct(item),
        };
      }
      return {
        cart_line_id: String(item.cartLineId),
        kind: "bundle" as const,
        id: Number(item.id),
        snapshot: {
          id: item?.id,
          bundle_name: item?.bundle_name ?? item?.product_name ?? "",
          description: item?.description ?? "",
          products: (item?.bundle_products || []).map((product: any) => ({
            bundle_product_id: product?.id,
            store_product_id:
              product?.store_product_id ?? product?.store_product_detail?.id,
            product: snapshotProduct(product?.store_product_detail || {}),
          })),
        },
      };
    })
    .filter((line) => Number.isFinite(line.id));
}

export async function fetchCartStoreChanges(
  storeId: number | string,
  cart: any[]
): Promise<CartLineChange[]> {
  if (!storeId || !cart?.length) return [];
  const response = await axios.post(
    `${process.env.REACT_APP_BASE_URL}/cart/check-changes`,
    {
      store_id: Number(storeId),
      lines: buildCartChangeLines(cart),
    }
  );
  const lines = response?.data?.data?.lines;
  return linkChangedProductsToBundles(cart, Array.isArray(lines) ? lines : []);
}

function lineNeedsResolution(line?: CartLineChange | null) {
  return Boolean(
    line && (line.unavailable || (line.changes && line.changes.length > 0))
  );
}

/**
 * A store edit on a single product must also block the bundle that owns it:
 * the product was assigned to that bundle, or the same product is inside it.
 */
export function linkChangedProductsToBundles(
  cart: any[],
  lines: CartLineChange[]
): CartLineChange[] {
  const byId = new Map<string, CartLineChange>();
  (lines || []).forEach((line) => {
    byId.set(String(line.cart_line_id), {
      ...line,
      changes: [...(line.changes || [])],
      relatedProductUpdates: [...(line.relatedProductUpdates || [])],
    });
  });

  const bundles = (cart || []).filter((row) => row && !row.isSingleProduct);

  (cart || []).forEach((row) => {
    if (!row?.isSingleProduct) return;
    const change = byId.get(String(row.cartLineId));
    if (!lineNeedsResolution(change) || !change) return;

    const parentId =
      row.parentBundleLineId != null && row.parentBundleLineId !== ""
        ? String(row.parentBundleLineId)
        : "";
    const targetIds = new Set<string>();
    if (parentId) targetIds.add(parentId);

    bundles.forEach((bundle) => {
      const contains = (bundle.bundle_products || []).some((product: any) => {
        const productId =
          product?.store_product_id ?? product?.store_product_detail?.id;
        return String(productId) === String(row.id);
      });
      if (contains && bundle.cartLineId != null) {
        targetIds.add(String(bundle.cartLineId));
      }
    });

    targetIds.forEach((bundleLineId) => {
      const bundleStillInCart = bundles.some(
        (bundle) => String(bundle.cartLineId) === bundleLineId
      );
      if (!bundleStillInCart) return;

      let bundleChange = byId.get(bundleLineId);
      if (!bundleChange) {
        bundleChange = {
          cart_line_id: bundleLineId,
          kind: "bundle",
          unavailable: false,
          changes: ["This Item has been changed"],
          current: null,
          relatedProductUpdates: [],
          synthetic: true,
        };
        byId.set(bundleLineId, bundleChange);
      } else if (!bundleChange.changes.length && !bundleChange.unavailable) {
        bundleChange.changes = ["This Item has been changed"];
      }

      const update: RelatedProductUpdate = {
        cart_line_id: String(row.cartLineId),
        store_product_id: row.id,
        unavailable: Boolean(change.unavailable),
        current: change.current,
        updateCartRow: parentId === bundleLineId,
      };
      const alreadyListed = (bundleChange.relatedProductUpdates || []).some(
        (item) => item.cart_line_id === update.cart_line_id
      );
      if (!alreadyListed) {
        bundleChange.relatedProductUpdates = [
          ...(bundleChange.relatedProductUpdates || []),
          update,
        ];
      }

      if (bundleChange.synthetic) {
        const updates = bundleChange.relatedProductUpdates || [];
        bundleChange.unavailable =
          updates.length > 0 && updates.every((item) => item.unavailable);
      }
    });
  });

  return [...byId.values()].filter((line) => lineNeedsResolution(line));
}

function clampQty(quantity: any, minimum: any, maximum: any) {
  let next = Number(quantity);
  if (!Number.isFinite(next) || next < 1) next = 1;
  const min =
    minimum == null || minimum === "" ? null : Number(minimum);
  const max =
    maximum == null || maximum === "" ? null : Number(maximum);
  if (min != null && Number.isFinite(min) && next < min) next = min;
  if (max != null && Number.isFinite(max) && max > 0 && next > max) next = max;
  return next;
}

function optionText(value: any) {
  return String(value ?? "").trim();
}

function sameOption(cartOption: any, liveOption: any) {
  if (
    cartOption?.global_option_id != null &&
    liveOption?.global_option_id != null &&
    String(cartOption.global_option_id) === String(liveOption.global_option_id)
  ) {
    return true;
  }
  if (
    cartOption?.store_option_id != null &&
    String(cartOption.store_option_id) === String(liveOption?.id)
  ) {
    return true;
  }
  if (
    cartOption?.id != null &&
    String(cartOption.id) === String(liveOption?.id)
  ) {
    return true;
  }
  return (
    optionText(cartOption?.display_name) !== "" &&
    optionText(cartOption.display_name) === optionText(liveOption?.display_name) &&
    String(cartOption?.option_type ?? "") === String(liveOption?.option_type ?? "")
  );
}

function keepSelectedItems(cartItems: any[], liveItems: any[]) {
  const liveById = new Map(
    (liveItems || []).map((item) => [String(item?.id), item])
  );
  return (cartItems || []).map((item) => {
    const live = liveById.get(String(item?.id));
    if (!live || live.option_name == null) return item;
    return { ...item, option_name: live.option_name };
  });
}

const CATALOG_FIELDS = [
  "product_name",
  "description",
  "style",
  "club_price",
  "base_price",
  "minimum_quantity",
  "maximum_quantity",
  "is_multiple_quantity",
  "product_shipping_weight",
  "product_shipping_unit",
  "store_product_images",
  "category",
  "manufacturer",
  "category_id",
  "manufacturer_id",
];

function mergeOptions(cartOptions: any[], liveOptions: any[]) {
  if (!liveOptions.length) return cartOptions || [];
  const used = new Set<number>();
  const merged = (cartOptions || []).map((cartOption) => {
    const index = liveOptions.findIndex(
      (liveOption, liveIndex) =>
        !used.has(liveIndex) && sameOption(cartOption, liveOption)
    );
    if (index < 0) return cartOption;
    used.add(index);
    const liveOption = liveOptions[index];
    const selectedData = cartOption?.selectedData;
    return {
      ...cartOption,
      display_name: liveOption.display_name ?? cartOption.display_name,
      internal_name: liveOption.internal_name ?? cartOption.internal_name,
      input_type: liveOption.input_type ?? cartOption.input_type,
      option_type: liveOption.option_type ?? cartOption.option_type,
      global_option_id:
        liveOption.global_option_id ?? cartOption.global_option_id,
      id: cartOption.id,
      store_option_items: keepSelectedItems(
        cartOption?.store_option_items || [],
        liveOption?.store_option_items || []
      ),
      selectedData:
        selectedData == null || selectedData === 0 ? "" : selectedData,
    };
  });

  liveOptions.forEach((liveOption, index) => {
    if (used.has(index)) return;
    merged.push({ ...liveOption, selectedData: "" });
  });
  return merged;
}

export function applyProductStoreUpdate(
  cartProduct: any,
  current: any,
  asBundleDetail = false
) {
  const next = { ...cartProduct };
  CATALOG_FIELDS.forEach((field) => {
    if (current && current[field] !== undefined) next[field] = current[field];
  });
  next.quantity = clampQty(
    cartProduct?.quantity,
    current?.minimum_quantity ?? cartProduct?.minimum_quantity,
    current?.maximum_quantity ?? cartProduct?.maximum_quantity
  );
  next.store_options = mergeOptions(
    cartProduct?.store_options || [],
    current?.store_options || []
  );

  if (!asBundleDetail) {
    next.isSingleProduct = true;
    next.cartItemId = cartProduct?.cartItemId;
    next.cartLineId = cartProduct?.cartLineId;
    next.parentBundleLineId = cartProduct?.parentBundleLineId ?? null;
  }
  return next;
}

export function applyBundleStoreUpdate(cartBundle: any, current: any) {
  const currentProducts = current?.bundle_products || [];
  const used = new Set<number>();
  let maxCartItemId = 0;
  (cartBundle?.bundle_products || []).forEach((product: any) => {
    maxCartItemId = Math.max(maxCartItemId, Number(product?.cartItemId) || 0);
  });

  const merged: any[] = [];
  (cartBundle?.bundle_products || []).forEach((cartProduct: any) => {
    const storeProductId =
      cartProduct?.store_product_id ?? cartProduct?.store_product_detail?.id;
    const index = currentProducts.findIndex(
      (product: any, productIndex: number) =>
        !used.has(productIndex) &&
        String(product?.store_product_id) === String(storeProductId)
    );
    if (index < 0) return;
    used.add(index);
    const currentProduct = currentProducts[index];
    merged.push({
      ...currentProduct,
      cartItemId: cartProduct?.cartItemId,
      store_product_detail: applyProductStoreUpdate(
        cartProduct?.store_product_detail || {},
        currentProduct.store_product_detail,
        true
      ),
    });
  });

  currentProducts.forEach((product: any, index: number) => {
    if (used.has(index)) return;
    maxCartItemId += 1;
    merged.push({
      ...product,
      cartItemId: maxCartItemId,
      store_product_detail: {
        ...product.store_product_detail,
        quantity: product.store_product_detail?.minimum_quantity || 1,
        store_options: (product.store_product_detail?.store_options || []).map(
          (option: any) => ({
            ...option,
            selectedData: "",
          })
        ),
      },
    });
  });

  return {
    ...cartBundle,
    bundle_name: current?.bundle_name,
    description: current?.description,
    bundle_products: merged,
  };
}

function applyRelatedProductUpdates(
  cart: any[],
  bundleIndex: number,
  line: CartLineChange
) {
  const bundle = cart[bundleIndex];
  if (!bundle || bundle.isSingleProduct) return;

  (line.relatedProductUpdates || []).forEach((update) => {
    if (update.unavailable || !update.current) return;

    if (update.updateCartRow) {
      const singleIndex = cart.findIndex(
        (row) => String(row?.cartLineId) === String(update.cart_line_id)
      );
      if (singleIndex >= 0 && cart[singleIndex]?.isSingleProduct) {
        cart[singleIndex] = applyProductStoreUpdate(
          cart[singleIndex],
          update.current
        );
      }
    }

    bundle.bundle_products = (bundle.bundle_products || []).map((product: any) => {
      const productId =
        product?.store_product_id ?? product?.store_product_detail?.id;
      if (String(productId) !== String(update.store_product_id)) return product;
      return {
        ...product,
        store_product_detail: applyProductStoreUpdate(
          product?.store_product_detail || {},
          update.current,
          true
        ),
      };
    });
  });
}

export function acceptCartLineChange(cartLineId: string | number, line: CartLineChange) {
  const canApplyRelated = (line?.relatedProductUpdates || []).some(
    (update) => update.current && !update.unavailable
  );
  if ((!line?.current && !canApplyRelated) || (line?.unavailable && !canApplyRelated)) {
    return readCart();
  }
  const cart = readCart();
  const index = cart.findIndex(
    (row) => String(row?.cartLineId) === String(cartLineId)
  );
  if (index < 0) return cart;
  if (line.current && !line.unavailable) {
    cart[index] = cart[index]?.isSingleProduct
      ? applyProductStoreUpdate(cart[index], line.current)
      : applyBundleStoreUpdate(cart[index], line.current);
  }
  if (!cart[index]?.isSingleProduct) {
    applyRelatedProductUpdates(cart, index, line);
  }
  writeCart(cart);
  return cart;
}

export function removeCartLineById(cartLineId: string | number) {
  const cart = readCart();
  const index = cart.findIndex(
    (row) => String(row?.cartLineId) === String(cartLineId)
  );
  if (index < 0) return cart;
  const row = cart[index];
  const removedLineId = row?.cartLineId;
  const isBundleRow = row && !row.isSingleProduct;
  cart.splice(index, 1);
  if (isBundleRow && removedLineId != null) {
    for (let i = cart.length - 1; i >= 0; i--) {
      const item = cart[i];
      if (
        item?.isSingleProduct &&
        Number(item.parentBundleLineId) === Number(removedLineId)
      ) {
        cart.splice(i, 1);
      }
    }
  }
  writeCart(cart);
  return cart;
}
